import type { KYCDocument } from '@pflegenavi/service-provider-frontend/api';

/**
 * Required KYC document types per MangoPay legal person type.
 *
 * The `name` values are translation keys rendered next to each document in the
 * profile KYC section.
 */
export const DOCUMENT_TYPES_BY_PERSON_TYPE: Record<
  string,
  Array<{ type: KYCDocument['Type']; name: string }>
> = {
  BUSINESS: [
    { type: 'IDENTITY_PROOF', name: 'kyc.identity.title.business' },
    {
      type: 'REGISTRATION_PROOF',
      name: 'kyc.registration-proof.title.business',
    },
    {
      type: 'ARTICLES_OF_ASSOCIATION',
      name: 'kyc.articles-of-association.title.business',
    },
    {
      type: 'SHAREHOLDER_DECLARATION',
      name: 'kyc.shareholder-declaration.title.business',
    },
  ],
  ORGANIZATION: [
    { type: 'IDENTITY_PROOF', name: 'kyc.identity.title.organization' },
    {
      type: 'REGISTRATION_PROOF',
      name: 'kyc.registration-proof.title.organization',
    },
    {
      type: 'ARTICLES_OF_ASSOCIATION',
      name: 'kyc.articles-of-association.title.organization',
    },
  ],
  SOLETRADER: [
    { type: 'IDENTITY_PROOF', name: 'kyc.identity.title.soletrader' },
    {
      type: 'REGISTRATION_PROOF',
      name: 'kyc.registration-proof.title.soletrader',
    },
  ],
  PARTNERSHIP: [], // Not applicable in Austria
};

/**
 * Resolve the representative status for a given KYC document type.
 *
 * Mirrors the logic used in the profile KYC section:
 * - If there is a validated document of the type, return that.
 * - Otherwise, if there is one waiting to be validated, return that.
 * - Otherwise, if there is a refused or out-of-date one, return that.
 * - Otherwise, return undefined (no document submitted for this type).
 *
 * Example:
 *
 *     resolveKycDocumentStatus(documents, 'IDENTITY_PROOF') // => 'VALIDATED'
 */
export const resolveKycDocumentStatus = (
  documents: KYCDocument[] | undefined,
  type: KYCDocument['Type']
): KYCDocument['Status'] | undefined => {
  return (
    documents?.find(
      (document) => document.Type === type && document.Status === 'VALIDATED'
    )?.Status ||
    documents?.find(
      (document) =>
        document.Type === type &&
        (document.Status === 'CREATED' ||
          document.Status === 'VALIDATION_ASKED')
    )?.Status ||
    documents?.find(
      (document) =>
        document.Type === type &&
        (document.Status === 'REFUSED' || document.Status === 'OUT_OF_DATE')
    )?.Status
  );
};
